import { Eye, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { API } from "../../lib/api";

function FileKindBadge({ kind }) {
  const { t } = useTranslation();
  const cfg = {
    cad3d_step:    { label: "STEP",                    cls: "bg-blue-500/10 text-blue-300 border-blue-500/40" },
    image:         { label: t("adminFiles.badgeImage"), cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40" },
    timestudy_pdf: { label: "PDF",                     cls: "bg-rose-500/10 text-rose-300 border-rose-500/40" },
    timestudy_doc: { label: t("adminFiles.badgeDoc"),   cls: "bg-amber-500/10 text-amber-300 border-amber-500/40" },
  }[kind] || { label: (kind || "FILE").toUpperCase(), cls: "bg-slate-500/10 text-slate-300 border-slate-500/40" };
  return <span className={`text-[10px] label-tech px-2 py-1 rounded border ${cfg.cls}`}>{cfg.label}</span>;
}

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function AdminFilesTab({ files, filesTotal }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "it";
  return (
    <div className="tech-card overflow-hidden" data-testid="admin-files">
      <div className="px-4 py-3 border-b border-slate-800 text-xs text-slate-400 font-mono-data">{t("adminFiles.storageInfo", { n: filesTotal })}</div>
      {files.length === 0 ? <div className="p-8 text-center text-slate-500 text-sm">{t("adminFiles.empty")}</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-slate-500 text-xs">
                <th className="text-left px-4 py-2 label-tech">{t("adminFiles.thUser")}</th>
                <th className="text-left px-4 py-2 label-tech">{t("adminFiles.thDate")}</th>
                <th className="text-left px-4 py-2 label-tech">{t("adminFiles.thType")}</th>
                <th className="text-left px-4 py-2 label-tech">{t("adminFiles.thFilename")}</th>
                <th className="text-right px-4 py-2 label-tech">{t("adminFiles.thSize")}</th>
                <th className="text-left px-4 py-2 label-tech">{t("adminFiles.thQuote")}</th>
                <th className="text-right px-4 py-2 label-tech">{t("adminFiles.thActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {files.map((f) => (
                <tr key={f.id} className="hover:bg-slate-800/30" data-testid={`admin-file-${f.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-slate-100 font-mono-data text-xs">{f.user_email || "—"}</div>
                    {f.user_name && <div className="text-[10px] text-slate-500">{f.user_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400 font-mono-data whitespace-nowrap">{new Date(f.created_at).toLocaleString(locale)}</td>
                  <td className="px-4 py-3"><FileKindBadge kind={f.kind}/></td>
                  <td className="px-4 py-3 text-xs text-slate-300 max-w-[220px] truncate" title={f.original_filename}>{f.original_filename || f.storage_path.split("/").pop()}</td>
                  <td className="px-4 py-3 text-right font-mono-data text-xs text-slate-300">{formatBytes(f.size)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400 max-w-[140px] truncate">{f.quote ? (f.quote.part_name || f.quote.id.slice(0, 8)) : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <a href={`${API}/files/${f.storage_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 mr-3" data-testid={`admin-file-view-${f.id}`}><Eye size={12}/> {t("adminFiles.view")}</a>
                    <a href={`${API}/files/${f.storage_path}?download=1`} className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300" data-testid={`admin-file-download-${f.id}`}><Download size={12}/> {t("adminFiles.download")}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
